import React, { memo, useState, useId } from "react";
import PropTypes from "prop-types";
import { Accordion, AccordionDetails, AccordionSummary, Grid, Divider, Box } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import BuildIcon from "@mui/icons-material/Build";
import StraightenIcon from "@mui/icons-material/Straighten";
import TuneIcon from "@mui/icons-material/Tune";
import HandymanIcon from "@mui/icons-material/Handyman";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

const BRAND = {
  main: "#036D3D",
  line: "rgba(3,109,61,.30)",
  bgLite: "rgba(3,109,61,.04)",
  badgeBg: "rgba(3,109,61,.12)",
};

// ======================
// Itens padrão
// ======================
const DEFAULT_ITEMS = [
  {
    icon: <TuneIcon />,
    title: "Torneamento",
    body: (
      <>
        <MKTypography variant="body2" color="text" paragraph sx={{ lineHeight: 1.75 }}>
          Processo para remoção de material em peças cilíndricas. Operações comuns:
        </MKTypography>
        <MKBox component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.5 } }}>
          <li><MKTypography variant="body2">Torneamento externo e interno</MKTypography></li>
          <li><MKTypography variant="body2">Roscas externa e interna</MKTypography></li>
          <li><MKTypography variant="body2">Torneamento cônico</MKTypography></li>
          <li><MKTypography variant="body2">Canais de lubrificação</MKTypography></li>
        </MKBox>
      </>
    ),
  },
  {
    icon: <HandymanIcon />,
    title: "Fresamento Convencional",
    body: (
      <MKTypography variant="body2" sx={{ lineHeight: 1.75 }}>
        Remoção de material por ferramenta de corte para peças com geometrias planas:
        quadradas, retangulares, sextavadas, etc.
      </MKTypography>
    ),
  },
  {
    icon: <PrecisionManufacturingIcon />,
    title: "Centro de Usinagem (CNC)",
    body: (
      <MKTypography variant="body2" sx={{ lineHeight: 1.75 }}>
        Mesmo conceito do fresamento, porém com execução automatizada via CNC — maior
        repetibilidade, precisão e produtividade. Operador atua na programação e logística de peças.
      </MKTypography>
    ),
  },
  {
    icon: <StraightenIcon />,
    title: "Retífica Plana",
    body: (
      <>
        <MKTypography variant="body2" paragraph sx={{ lineHeight: 1.75 }}>
          Acabamento com remoção mínima usando rebolos em geometrias planas. Exemplos:
        </MKTypography>
        <MKBox component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.5 } }}>
          <li><MKTypography variant="body2">Faces de cabeçotes</MKTypography></li>
          <li><MKTypography variant="body2">Faces laterais de engrenagens</MKTypography></li>
          <li><MKTypography variant="body2">Bases para medição</MKTypography></li>
          <li><MKTypography variant="body2">Faces laterais de anéis de pistões</MKTypography></li>
          <li><MKTypography variant="body2">Lâminas para medir folga</MKTypography></li>
        </MKBox>
      </>
    ),
  },
  {
    icon: <StraightenIcon />,
    title: "Retífica Cilíndrica",
    body: (
      <>
        <MKTypography variant="body2" paragraph sx={{ lineHeight: 1.75 }}>
          Acabamento com rebolos em geometrias circulares. Exemplos:
        </MKTypography>
        <MKBox component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.5 } }}>
          <li><MKTypography variant="body2">Diâmetros internos/externos de rolamentos</MKTypography></li>
          <li><MKTypography variant="body2">Diâmetros internos de engrenagens</MKTypography></li>
          <li><MKTypography variant="body2">Calibradores tampão (PNP)</MKTypography></li>
          <li><MKTypography variant="body2">Eixos cilíndricos e cônicos</MKTypography></li>
        </MKBox>
      </>
    ),
  },
  {
    icon: <BuildIcon />,
    title: "Ferramentaria e Ajustagem",
    body: (
      <>
        <MKTypography variant="body2" paragraph sx={{ lineHeight: 1.75 }}>
          Ajustes finos de bancada para montagem e acabamento de peças e conjuntos. Exemplos:
        </MKTypography>
        <MKBox component="ul" sx={{ m: 0, pl: 2, "& li": { mb: 0.5 } }}>
          <li><MKTypography variant="body2">Matrizes de forjamento</MKTypography></li>
          <li><MKTypography variant="body2">Moldes de injeção plástica</MKTypography></li>
          <li><MKTypography variant="body2">Moldes de fundição</MKTypography></li>
          <li><MKTypography variant="body2">Dispositivos de fixação de usinagem</MKTypography></li>
        </MKBox>
      </>
    ),
  },
];

// ======================
// Componente principal
// ======================
function ServicesAccordions({ items = DEFAULT_ITEMS, defaultExpandedIndex = 0 }) {
  const baseId = useId();
  const [expanded, setExpanded] = useState(defaultExpandedIndex);

  return (
    <Grid container spacing={2.5}>
      <Box sx={{ pt: 3, pl:2 }}>
        <MKTypography variant="h5" fontWeight="bold" sx={{ color: BRAND.dark, mb: 1 }}>
          Serviços:
        </MKTypography>
      </Box>
      {items.map((s, idx) => {
        const panelId = `${baseId}-panel-${idx}`;
        return (
          <Grid key={s.title} item xs={12}>
            <Accordion
              expanded={expanded === idx}
              onChange={(_, isExp) => setExpanded(isExp ? idx : false)}
              disableGutters
              elevation={0}
              sx={{
                border: `1px solid ${BRAND.line}`,
                borderRadius: 2,
                overflow: "hidden",
                transition: "border-color .2s ease, box-shadow .2s ease",
                "&:hover": { borderColor: BRAND.main, boxShadow: "0 6px 18px rgba(0,0,0,.06)" },
              }}
            >
              <AccordionSummary
                id={`${panelId}-summary`}
                aria-controls={`${panelId}-content`}
                expandIcon={<ExpandMoreIcon sx={{ color: BRAND.main }} />}
                sx={{
                  bgcolor: expanded === idx ? BRAND.bgLite : "transparent",
                  minHeight: 44,
                  "& .MuiAccordionSummary-content": { my: 0.5 },
                }}
              >
                <MKBox sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <MKBox
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      bgcolor: BRAND.badgeBg,
                      color: BRAND.main,
                    }}
                  >
                    {s.icon}
                  </MKBox>
                  <MKTypography variant="button" sx={{ fontWeight: 800, letterSpacing: ".4px" }}>
                    {s.title}
                  </MKTypography>
                </MKBox>
              </AccordionSummary>

              <AccordionDetails id={`${panelId}-content`}>
                <Divider sx={{ mb: 1, borderColor: BRAND.line, opacity: 0.4 }} />
                {s.body}
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      })}
    </Grid>
  );
}

ServicesAccordions.propTypes = {
  items: PropTypes.array,
  defaultExpandedIndex: PropTypes.number, // -1 para todos fechados
};

export default memo(ServicesAccordions);
